import type { PixelEventType } from "./types.js";

/**
 * Idempotency-key derivation (event-types.md §"Idempotency key derivation"). The backend dedups
 * on this, so the SAME logical event must always produce the SAME key:
 *   - product_view → `visitor:pv:session:product` (enforces one-per-product-per-session)
 *   - other client events → sha1(visitor + ":" + event + ":" + floor(epoch_ms/1000))
 *     (collapses double-fires within the same second)
 * sha1 via Web Crypto (present in browsers and Node 22 `globalThis.crypto.subtle`).
 */
export interface IdemInput {
  event: PixelEventType;
  visitorId: string;
  sessionId: string;
  nowMs: number;
  productId?: string | undefined;
  /** Disambiguates events that may legitimately fire more than once per second (e.g. scroll milestones). */
  disambiguator?: string | undefined;
}

export async function deriveIdempotencyKey(input: IdemInput): Promise<string> {
  if (input.event === "product_view" && input.productId) {
    return `${input.visitorId}:pv:${input.sessionId}:${input.productId}`;
  }
  if (input.disambiguator) {
    return sha1Hex(`${input.visitorId}:${input.sessionId}:${input.event}:${input.disambiguator}`);
  }
  const second = Math.floor(input.nowMs / 1000);
  return sha1Hex(`${input.visitorId}:${input.event}:${second}`);
}

export async function sha1Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-1", bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
